import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from '../config/api';

interface UploadDisabledStatus {
  isDisabled: boolean;
  isLoading: boolean;
}

interface UploadPermissions {
  user_upload_allowed: boolean;
}

export const useUploadDisabled = (): UploadDisabledStatus => {
  const [isDisabled, setIsDisabled] = useState<boolean>(true); // Default to disabled
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { getToken, isSignedIn, isLoaded } = useAuth();

  const checkUploadPermissions = useCallback(async () => {
    if (!isLoaded) return;
    
    if (!isSignedIn) {
      // If user is not signed in, fall back to environment variable
      const disableUploads = process.env.REACT_APP_DISABLE_UPLOADS;
      const shouldDisable = disableUploads?.toLowerCase() === 'true';
      setIsDisabled(shouldDisable);
      setIsLoading(false);
      return;
    }

    try {
      // Get auth token
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Fetch upload permissions from backend using axios like other API calls
      const response = await axios.get('/api/upload-permissions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const permissions: UploadPermissions = response.data;
      
      if (permissions.user_upload_allowed) {
        // User has database override - always allow
        setIsDisabled(false);
      } else {
        // No database override - check env var
        const shouldDisable = process.env.REACT_APP_DISABLE_UPLOADS?.toLowerCase() === 'true';
        setIsDisabled(shouldDisable);
      }
      
    } catch (error) {
      console.error('Failed to check upload permissions:', error);
      // Fall back to environment variable on error
      const shouldDisable = process.env.REACT_APP_DISABLE_UPLOADS?.toLowerCase() === 'true';
      setIsDisabled(shouldDisable);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, isSignedIn, isLoaded]);

  useEffect(() => {
    checkUploadPermissions();
  }, [checkUploadPermissions]);

  return {
    isDisabled,
    isLoading
  };
};
